import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * iOS Smart App Banner — the top-of-page strip iOS Safari shows offering to
 * open the page in our native app. The static <meta name="apple-itunes-app">
 * in index.html sets the app-id and a fallback app-argument; this hook keeps
 * the app-argument in sync with the current route on every navigation so
 * tapping "Open" lands the user on the same content they were viewing.
 *
 * Only runs on web. Native (Capacitor) is the app — no banner to show.
 *
 * Allowlist of hostnames prevents pointing the banner at preview / local URLs
 * that the installed app couldn't open via Universal Links anyway.
 */

const APP_ID = '6467179371';
const FALLBACK_ARGUMENT = 'https://globethrivers.com/';
const ALLOWED_HOSTS = new Set([
    'globethrivers.com',
    'app.globethrivers.com',
    'app-staging.globethrivers.com',
]);

function computeAppArgument(): string {
    if (typeof window === 'undefined') return FALLBACK_ARGUMENT;
    try {
        const current = new URL(window.location.href);
        if (ALLOWED_HOSTS.has(current.hostname)) {
            return `${current.origin}${current.pathname}${current.search}${current.hash}`;
        }
    } catch {
        /* fall through to production root */
    }
    return FALLBACK_ARGUMENT;
}

export function useSmartAppBanner(): void {
    const location = useLocation();

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const meta = document.querySelector<HTMLMetaElement>('meta[name="apple-itunes-app"]');
        if (!meta) return;
        meta.setAttribute('content', `app-id=${APP_ID}, app-argument=${computeAppArgument()}`);
    }, [location.pathname, location.search, location.hash]);
}
